/**
 * Content Store for Agent Framework V2.
 *
 * Provides generic versioned storage for all content types.
 * Only accepts valid content (validates via adapter before storing).
 *
 * Persistence: each content item is stored as its own IndexedDB entry
 * using idb-keyval directly (no Zustand persist middleware). This avoids
 * the "Invalid string length" error that occurs when JSON.stringify is
 * called on the entire store (which can exceed V8's string limit with
 * accumulated PDF base64 data).
 *
 * PDFs are stored in IDB as raw Uint8Array (33% smaller than base64).
 * Conversion happens at the IDB boundary only — in-memory format stays
 * base64 so all existing callers work unchanged.
 */

import { create } from 'zustand';
import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from 'idb-keyval';
import { nanoid } from 'nanoid';
import type {
  ContentStoreInterface,
  Content,
  ContentSummary,
  Commit,
  CommitResult,
  ContentTypeAdapter,
} from '../lib/agent/types';

// ============================================================================
// Binary Helpers
// ============================================================================

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

// ============================================================================
// Store State
// ============================================================================

interface ContentStoreState {
  /** All content items, keyed by ID */
  contents: Record<string, Content>;

  /** Version history for each content item */
  history: Record<string, Commit[]>;
}

interface ContentStoreActions {
  /** Commit content to the store (validates via adapter first) */
  commit: (
    id: string,
    content: unknown,
    type: string,
    metadata?: { source?: 'generated' | 'uploaded'; conversationId?: string }
  ) => CommitResult;

  /** Get content by ID */
  get: (id: string) => Content | null;

  /** List content summaries, optionally filtered by conversationId */
  list: (conversationId?: string) => ContentSummary[];

  /** Get version history for content */
  getHistory: (id: string) => Commit[];

  /** Delete content by ID */
  delete: (id: string) => boolean;

  /** Delete all content scoped to a conversation/project namespace. Returns deleted IDs. */
  deleteByConversationId: (conversationId: string) => string[];

  /** Clear all content (for testing) */
  clear: () => void;

  /** Lazy-load PDF base64 data from IDB on demand */
  loadPdfData: (id: string) => Promise<string | null>;

  /** Load PDF as raw Uint8Array from IDB (no base64 conversion — avoids OOM on large PDFs) */
  loadPdfBinary: (id: string) => Promise<Uint8Array | null>;
}

type ContentStore = ContentStoreState & ContentStoreActions;

// ============================================================================
// Adapter Registry
// ============================================================================

// Adapters are registered here by the adapter registry module
// This allows the store to validate content before storing
let adapters: Map<string, ContentTypeAdapter> = new Map();

/**
 * Register adapters with the content store.
 * Called by the adapter registry during initialization.
 */
export function registerAdapters(adapterList: ContentTypeAdapter[]): void {
  adapters = new Map(adapterList.map(a => [a.type, a]));
}

/**
 * Get the adapter for a content type.
 */
export function getAdapter(type: string): ContentTypeAdapter | undefined {
  return adapters.get(type);
}

// ============================================================================
// IDB Persistence Helpers
// ============================================================================

const CONTENT_ITEM_PREFIX = 'content-item:';
const CONTENT_HISTORY_PREFIX = 'content-history:';
const PPTX_BINARY_PREFIX = 'pptx-binary:';
const DOCX_BINARY_PREFIX = 'docx-binary:';
const IMAGE_BINARY_PREFIX = 'image-binary:';

export async function storePptxBinary(presentationId: string, data: ArrayBuffer): Promise<void> {
  await idbSet(`${PPTX_BINARY_PREFIX}${presentationId}`, new Uint8Array(data));
}

export async function loadPptxBinary(presentationId: string): Promise<ArrayBuffer | null> {
  const data = await idbGet(`${PPTX_BINARY_PREFIX}${presentationId}`);
  if (data instanceof Uint8Array) return data.buffer as ArrayBuffer;
  return null;
}

export async function deletePptxBinary(presentationId: string): Promise<void> {
  await idbDel(`${PPTX_BINARY_PREFIX}${presentationId}`);
}

export async function storeImageBinary(imageId: string, base64Data: string): Promise<void> {
  await idbSet(`${IMAGE_BINARY_PREFIX}${imageId}`, base64ToUint8Array(base64Data));
}

export async function loadImageBinary(imageId: string): Promise<string | null> {
  const data = await idbGet(`${IMAGE_BINARY_PREFIX}${imageId}`);
  if (data instanceof Uint8Array) return uint8ArrayToBase64(data);
  return null;
}

export async function deleteImageBinary(imageId: string): Promise<void> {
  await idbDel(`${IMAGE_BINARY_PREFIX}${imageId}`);
}

export async function storeDocxBinary(documentId: string, data: ArrayBuffer): Promise<void> {
  await idbSet(`${DOCX_BINARY_PREFIX}${documentId}`, new Uint8Array(data));
}

export async function loadDocxBinary(documentId: string): Promise<ArrayBuffer | null> {
  const data = await idbGet(`${DOCX_BINARY_PREFIX}${documentId}`);
  if (data instanceof Uint8Array) return data.buffer as ArrayBuffer;
  return null;
}

export async function deleteDocxBinary(documentId: string): Promise<void> {
  await idbDel(`${DOCX_BINARY_PREFIX}${documentId}`);
}

// ── Document/Presentation embedded images ─────────────────────────────────

const DOC_IMAGE_PREFIX = 'doc-img:';

/** Store an embedded image extracted from a DOCX/PPTX file. */
export async function storeDocumentImage(
  contentId: string,
  imageName: string,
  base64Data: string,
  mimeType: string,
): Promise<void> {
  await idbSet(`${DOC_IMAGE_PREFIX}${contentId}:${imageName}`, { data: base64Data, mimeType });
}

/** Load an embedded image by content ID and name. */
export async function loadDocumentImage(
  contentId: string,
  imageName: string,
): Promise<{ data: string; mimeType: string } | null> {
  const entry = await idbGet(`${DOC_IMAGE_PREFIX}${contentId}:${imageName}`);
  if (entry && typeof entry === 'object' && 'data' in entry) {
    return entry as { data: string; mimeType: string };
  }
  return null;
}

/** List all embedded image names for a content ID. */
export async function listDocumentImages(contentId: string): Promise<string[]> {
  const prefix = `${DOC_IMAGE_PREFIX}${contentId}:`;
  const allKeys = await idbKeys();
  return allKeys
    .filter((k): k is string => typeof k === 'string' && k.startsWith(prefix))
    .map(k => k.slice(prefix.length));
}

/** Persist a content item to IDB (fire-and-forget). PDFs get binary optimization. */
function persistContentItem(newContent: Content): void {
  const idbContent =
    newContent.type === 'pdf' && (newContent.content as Record<string, unknown>)?.data
      ? {
          ...newContent,
          content: {
            ...(newContent.content as Record<string, unknown>),
            data: base64ToUint8Array((newContent.content as Record<string, unknown>).data as string),
          },
        }
      : newContent;

  idbSet(`${CONTENT_ITEM_PREFIX}${newContent.id}`, idbContent).catch(err => {
    console.error('[ContentStore] IDB persist FAILED for', newContent.id, err);
  });
}

/** Persist history for a content item to IDB (fire-and-forget). */
function persistHistory(id: string, commits: Commit[]): void {
  idbSet(`${CONTENT_HISTORY_PREFIX}${id}`, commits).catch(err =>
    console.error('[ContentStore] IDB history persist failed for', id, err)
  );
}

function isContentSidecarKey(key: string, id: string): boolean {
  return (
    key === `${PPTX_BINARY_PREFIX}${id}` ||
    key === `${DOCX_BINARY_PREFIX}${id}` ||
    key === `${IMAGE_BINARY_PREFIX}${id}` ||
    key.startsWith(`${DOC_IMAGE_PREFIX}${id}:`)
  );
}

/** Delete a content item, its history, and sidecar binaries from IDB (fire-and-forget). */
function deleteFromIDB(id: string): void {
  idbDel(`${CONTENT_ITEM_PREFIX}${id}`).catch(err =>
    console.error('[ContentStore] IDB delete failed for', id, err)
  );
  idbDel(`${CONTENT_HISTORY_PREFIX}${id}`).catch(err =>
    console.error('[ContentStore] IDB history delete failed for', id, err)
  );
  idbKeys()
    .then((allKeys) => {
      for (const key of allKeys) {
        if (typeof key === 'string' && isContentSidecarKey(key, id)) {
          idbDel(key).catch(() => {});
        }
      }
    })
    .catch(() => {});
}

function isAnyContentStorageKey(key: string): boolean {
  return (
    key.startsWith(CONTENT_ITEM_PREFIX) ||
    key.startsWith(CONTENT_HISTORY_PREFIX) ||
    key.startsWith(PPTX_BINARY_PREFIX) ||
    key.startsWith(DOCX_BINARY_PREFIX) ||
    key.startsWith(IMAGE_BINARY_PREFIX) ||
    key.startsWith(DOC_IMAGE_PREFIX)
  );
}

// ============================================================================
// Zustand Store (no persist middleware)
// ============================================================================

export const useContentStore = create<ContentStore>()(
  (set, get) => ({
    contents: {},
    history: {},

    commit: (id, content, type, metadata) => {
      // Get adapter for validation
      const adapter = adapters.get(type);
      if (!adapter) {
        return {
          success: false,
          validationErrors: [{ path: '', message: `No adapter registered for type: ${type}` }],
        };
      }

      // Validate content
      const validation = adapter.validate(content);
      if (!validation.valid) {
        return {
          success: false,
          validationErrors: validation.errors?.map(e => ({ path: e.path, message: e.message })) || [],
        };
      }

      const commitId = nanoid(10);
      const now = Date.now();

      const existing = get().contents[id];

      // Require conversationId for new content, inherit from existing if updating
      const conversationId = metadata?.conversationId || existing?.conversationId;
      if (!conversationId) {
        return {
          success: false,
          validationErrors: [{ path: '', message: 'conversationId is required for new content' }],
        };
      }

      const newContent: Content = {
        id,
        type,
        content,
        commitId,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        source: metadata?.source,
        conversationId,
      };

      // Create commit entry for history (skip full content for PDFs to save memory)
      const commit: Commit = {
        commitId,
        content: type === 'pdf' ? { type: 'pdf', title: (content as { title?: string }).title } : content,
        timestamp: now,
      };

      set(state => {
        const newHistory = [...(state.history[id] || []), commit];
        // Fire-and-forget IDB persistence
        persistContentItem(newContent);
        persistHistory(id, newHistory);
        return {
          contents: { ...state.contents, [id]: newContent },
          history: { ...state.history, [id]: newHistory },
        };
      });

      return { success: true, commitId };
    },

    get: (id) => {
      return get().contents[id] || null;
    },

    list: (conversationId?: string) => {
      const contents = get().contents;
      return Object.values(contents)
        .filter(c => !conversationId || c.conversationId === conversationId)
        .map(c => {
          const adapter = adapters.get(c.type);
          const summary = adapter?.getSummary(c.content);
          return {
            id: c.id,
            type: c.type,
            title: summary?.title || 'Untitled',
          };
        });
    },

    getHistory: (id) => {
      return get().history[id] || [];
    },

    delete: (id) => {
      const exists = !!get().contents[id];
      if (!exists) return false;

      set(state => {
        const { [id]: _, ...remainingContents } = state.contents;
        const { [id]: __, ...remainingHistory } = state.history;
        // Fire-and-forget IDB deletion
        deleteFromIDB(id);
        return {
          contents: remainingContents,
          history: remainingHistory,
        };
      });

      return true;
    },

    deleteByConversationId: (conversationId) => {
      const ids = Object.values(get().contents)
        .filter((content) => content.conversationId === conversationId)
        .map((content) => content.id);
      if (ids.length === 0) return [];

      set((state) => {
        const remainingContents = { ...state.contents };
        const remainingHistory = { ...state.history };
        for (const id of ids) {
          delete remainingContents[id];
          delete remainingHistory[id];
          deleteFromIDB(id);
        }
        return {
          contents: remainingContents,
          history: remainingHistory,
        };
      });

      return ids;
    },

    clear: () => {
      // Clear in-memory state immediately
      const currentIds = Object.keys(get().contents);
      set({ contents: {}, history: {} });

      // Clear IDB entries (fire-and-forget)
      for (const id of currentIds) {
        deleteFromIDB(id);
      }

      // Clear every content-related IDB entry, including sidecar binaries.
      idbKeys().then(allKeys => {
        for (const key of allKeys) {
          if (typeof key === 'string' && isAnyContentStorageKey(key)) {
            idbDel(key).catch(() => {});
          }
        }
      }).catch(() => {});
    },

    loadPdfData: async (id: string): Promise<string | null> => {
      // If already in memory, return it
      const existing = get().contents[id];
      if (!existing) return null;
      const existingData = (existing.content as Record<string, unknown>)?.data;
      if (typeof existingData === 'string' && existingData.length > 0) {
        return existingData;
      }

      // Fetch from IDB
      const idbItem = await idbGet(`${CONTENT_ITEM_PREFIX}${id}`);
      if (!idbItem?.content?.data) return null;

      // Convert Uint8Array→base64 if needed
      const base64 = idbItem.content.data instanceof Uint8Array
        ? uint8ArrayToBase64(idbItem.content.data)
        : idbItem.content.data;

      // Patch into in-memory store
      set(state => ({
        contents: {
          ...state.contents,
          [id]: {
            ...state.contents[id],
            content: { ...(state.contents[id].content as Record<string, unknown>), data: base64 },
          },
        },
      }));

      return base64;
    },

    loadPdfBinary: async (id: string): Promise<Uint8Array | null> => {
      const existing = get().contents[id];
      if (!existing) return null;

      // If in-memory data is a base64 string (right after upload), convert it
      const existingData = (existing.content as Record<string, unknown>)?.data;
      if (typeof existingData === 'string' && existingData.length > 0) {
        return base64ToUint8Array(existingData);
      }

      // Fetch from IDB — return Uint8Array directly, no base64 conversion
      const idbItem = await idbGet(`${CONTENT_ITEM_PREFIX}${id}`);
      if (!idbItem?.content?.data) return null;

      if (idbItem.content.data instanceof Uint8Array) {
        return idbItem.content.data;
      }

      // Fallback: if stored as base64 string (shouldn't happen, but be safe)
      if (typeof idbItem.content.data === 'string') {
        return base64ToUint8Array(idbItem.content.data);
      }

      return null;
    },
  })
);

// ============================================================================
// Hydration from IDB
// ============================================================================

/**
 * Migrate old monolithic Zustand persist storage to per-item IDB entries.
 * Runs once — after migration the old key is gone, so subsequent startups skip it.
 */
async function migrateOldStorage(): Promise<void> {
  const oldData = await idbGet('claude-content-storage');
  if (!oldData) return; // nothing to migrate

  try {
    const parsed = typeof oldData === 'string' ? JSON.parse(oldData) : oldData;
    const state = parsed?.state ?? parsed;
    const contents = state?.contents ?? {};
    const history = state?.history ?? {};

    const contentEntries = Object.entries(contents);
    const historyEntries = Object.entries(history);

    // Write each item individually
    await Promise.all([
      ...contentEntries.map(([id, item]) => idbSet(`${CONTENT_ITEM_PREFIX}${id}`, item)),
      ...historyEntries.map(([id, hist]) => idbSet(`${CONTENT_HISTORY_PREFIX}${id}`, hist)),
    ]);

    // Remove old blob
    await idbDel('claude-content-storage');
  } catch (err) {
    console.error('[ContentStore] Migration failed:', err);
  }
}

/**
 * Hydrate the content store from individual IDB entries.
 * Called at module load time (same timing as Zustand persist would hydrate).
 */
async function hydrateFromIDB(): Promise<void> {
  // First, migrate any old monolithic storage
  await migrateOldStorage();

  const allKeys = await idbKeys();
  const contentKeys = allKeys.filter(
    (k): k is string => typeof k === 'string' && k.startsWith(CONTENT_ITEM_PREFIX)
  );
  const historyKeys = allKeys.filter(
    (k): k is string => typeof k === 'string' && k.startsWith(CONTENT_HISTORY_PREFIX)
  );


  const contents: Record<string, Content> = {};
  const history: Record<string, Commit[]> = {};

  for (const key of contentKeys) {
    try {
      const item = await idbGet(key);
      if (!item) continue;

      // Strip PDF data on hydration — loaded on demand via loadPdfData()
      if (item.type === 'pdf' && item.content?.data) {
        item.content = { ...item.content, data: undefined };
      }

      contents[item.id] = item;
    } catch (err) {
      console.error(`[ContentStore] hydrate FAILED for ${key}:`, err);
    }
  }

  await Promise.all(
    historyKeys.map(async key => {
      const id = key.replace(CONTENT_HISTORY_PREFIX, '');
      const hist = await idbGet(key);
      if (hist) history[id] = hist;
    })
  );

  useContentStore.setState({ contents, history });
}

// Kick off hydration at module load time
hydrateFromIDB();

// ============================================================================
// ContentStoreInterface Implementation
// ============================================================================

/**
 * Create a ContentStoreInterface from the Zustand store.
 * This is used by adapters and the orchestrator.
 */
export function getContentStoreInterface(): ContentStoreInterface {
  const store = useContentStore.getState();
  return {
    commit: store.commit,
    get: store.get,
    list: (conversationId?: string) => store.list(conversationId),
    getHistory: store.getHistory,
    delete: store.delete,
    loadPdfData: store.loadPdfData,
    loadPdfBinary: store.loadPdfBinary,
  };
}
